import { createFileRoute } from '@tanstack/react-router'
import InvoiceListPageHeader from '../../Pages/InvoiceListPage/InvoiceListPageHeader'
import InvoiceListPage from '../../Pages/InvoiceListPage/InvoiceListPage'
import PageBody from '../../Pages/Layout/PageBody'
import SessionStore from '../../State/SessionStore'
import LoadingSpinner from '../../Components/LoadingSpinner'
import ErrorComponent from '../../Components/ErrorComponent'
import {
    canViewRoute,
    migratedToV2,
} from '../../State/Permissions/HasPermissions'
import PermissionDenied from '../../Pages/PermissionDenied'
import { setLayoutDefaults } from '@2/layout/layout-store'
import { getCacheItemById } from '@2/cache'
import {
    setOrganisationReport,
    setReportData,
} from '@2/pages/invoice-report/invoice-report-store'
import { trpc } from '../../system/trpc'
import { InvoiceReportColumns } from '../../reports/Invoices/InvoiceReportColumns'
import { InvoiceReportsPageV2 } from '@2/pages/invoice-report/invoice-report-page'
import { dateStringLookup } from '../../Components/Filters'
import { queryClient } from '../../App'
import { format } from 'date-fns'
import { PageBody as V2PageBody } from '@2/layout/page-body'
import { processTRPCData } from '../../Queries/fetchData'

export const Route = createFileRoute('/_private/invoices/')({
    beforeLoad: ({ params, search }) => {
        if (!canViewRoute(SessionStore.user, 'invoiceList', params))
            throw new Error('Permission Denied')
        const report = getCacheItemById('organisationReports', search.report)
        setLayoutDefaults({
            pageTitle: report?.name || 'Invoice Report',
            subMenu: 'invoices',
        })
    },
    errorComponent: ({ error, reset }) => {
        if (error.message === 'Permission Denied') return <PermissionDenied />
        return <ErrorComponent error={error} reset={reset} />
    },
    loader: async ({ location }) => await loader(location.search),
    loaderDeps: ({ search: { report } }) => ({
        report,
    }),
    pendingComponent: LoadingSpinner,
    component: Wrapper,
})

const loader = async ({ report }) => {
    const reportId = report || SessionStore.organisation?.defaultInvoiceReportId
    if (migratedToV2()) {
        const res = reportId
            ? await trpc.invoiceReport.getOrganisationReport.query({
                  reportId,
              })
            : null

        const organisationReport =
            res || SessionStore.organisation.defaultInvoiceReport
        let dateRange = [null, null]
        if (organisationReport.options?.dateRange) {
            dateRange =
                dateStringLookup[organisationReport.options?.dateRange]?.(
                    organisationReport.options?.fortnightType
                ) || organisationReport.options?.dateRange
        }
        const { columns, filters, groupBy, sortBy, options, name } =
            organisationReport
        setOrganisationReport(
            {
                columns,
                filters,
                groupBy,
                organisationId: SessionStore.organisationId,
                sortBy,
                dateRange,
                invoiceDateType: SessionStore.settings.reportInvoiceDateType,
                id: reportId,
                options,
                name,
            },
            { needsRefetch: false }
        )
        const cacheData = await queryClient.fetchQuery({
            queryKey: ['invoiceReportCache', reportId],
            queryFn: () =>
                trpc.invoiceReport.getReportsData.query({
                    organisationId: SessionStore.organisationId,
                    userId: SessionStore.user?.id,
                    columns: organisationReport?.columns.filter(
                        (c) =>
                            !InvoiceReportColumns(reportId)[c]?.permissions ||
                            InvoiceReportColumns(reportId)[c].permissions()
                    ),
                    filters: organisationReport?.filters.map((f) => {
                        if (
                            InvoiceReportColumns(reportId)[f.column]?.type ===
                            'date'
                        ) {
                            let value = dateStringLookup[f.value]?.() || f.value
                            if (Array.isArray(value)) {
                                value = value.map((d) =>
                                    d ? format(d, 'yyyy-MM-dd') : null
                                )
                            } else {
                                value = value
                                    ? format(value, 'yyyy-MM-dd')
                                    : null
                            }
                            return {
                                ...f,
                                value,
                            }
                        } else if (
                            InvoiceReportColumns(reportId)[f.column]?.type ===
                            'invoice'
                        ) {
                            let value = f.value
                            if (Array.isArray(value)) {
                                value = value.map((d) => {
                                    d = new Date(d)
                                    return d && d.getTime()
                                        ? format(d, 'yyyy-MM-dd HH:mm')
                                        : null
                                })
                            } else {
                                value = new Date(value)
                                value =
                                    value && value.getTime()
                                        ? format(value, 'yyyy-MM-dd HH:mm')
                                        : null
                            }
                            return {
                                ...f,
                                value,
                            }
                        } else {
                            return f
                        }
                    }),
                    sortBy: organisationReport?.sortBy || [],
                    groupBy: organisationReport?.groupBy || [],
                    dateRange,
                    invoiceDateType:
                        SessionStore.settings.reportInvoiceDateType,
                }),
            staleTime: 5 * 60 * 1000,
        })

        setReportData(cacheData)
    }
    const data = await trpc.legacy.getInvoiceListPageData.query({ reportId })
    return processTRPCData(data)
}

function Wrapper() {
    const { report } = Route.useSearch()
    if (migratedToV2()) {
        return (
            <V2PageBody>
                <InvoiceReportsPageV2 />
            </V2PageBody>
        )
    }
    return (
        <>
            <InvoiceListPageHeader id={report} />
            <PageBody>
                <InvoiceListPage id={report} />
            </PageBody>
        </>
    )
}
